import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

/**
 * Alapbeállítások – DEBRECEN
 */
const SITE_NAME = "Pizza Home Debrecen";
const BASE_URL = "https://debrecenipizza.hu";
const DEFAULT_IMAGE = "/android-chrome-512x512.png"; // /public alól szolgálva
const DEFAULT_LOCALE = "hu_HU";

/**
 * Segédfüggvények
 */
function ensureLeadingSlash(p = "/") {
  if (!p) return "/";
  return p.startsWith("/") ? p : `/${p}`;
}

function buildCanonical(base, path) {
  const safePath = ensureLeadingSlash(path || "/");
  return `${base}${safePath}`;
}

function toAbsoluteUrl(possiblyRelative, base = BASE_URL) {
  if (!possiblyRelative) return base;
  if (/^https?:\/\//i.test(possiblyRelative)) return possiblyRelative;
  return `${base}${ensureLeadingSlash(possiblyRelative)}`;
}

/**
 * Seo komponens (Debrecen verzió)
 */
export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  noindex = false,
  structuredData = null,
}) {
  const { pathname } = useLocation();

  const effectivePath = path || pathname || "/";
  const canonical = buildCanonical(BASE_URL, effectivePath);
  const ogImage = toAbsoluteUrl(image);

  // hreflang
  const hrefLangs = [{ lang: "hu-HU", href: canonical }];

  const ldPayload = Array.isArray(structuredData)
    ? structuredData
    : structuredData
    ? [structuredData]
    : [];

  return (
    <Helmet defaultTitle={SITE_NAME} titleTemplate="%s | Pizza Home Debrecen">
      {/* Title + Description */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      {/* Robots */}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Alap meta */}
      <meta name="author" content={SITE_NAME} />
      <meta
        name="keywords"
        content="Pizza Debrecen, vékonytésztás pizza Debrecen, gyros Debrecen, pizza házhozszállítás Debrecen, pizzéria Debrecen"
      />

      {/* Canonical + hreflang */}
      <link rel="canonical" href={canonical} />
      {hrefLangs.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/`} />

      {/* JSON-LD */}
      {ldPayload.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={DEFAULT_LOCALE} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
    </Helmet>
  );
}
